// React
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { NumericFormat } from "react-number-format";

// MUI
import {
    Box,
    TextField,
    Grid,
    Divider,
    Card,
    CardHeader,
    CardContent,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    Select,
    MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

/** MUI icons */
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PREFIX = "KiiperBanking";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
}));

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

const defaultState = {
    ogFee: 0,
    hours: 1,
    fee: 0,
    discount: 0,
    totalFee: 0,
    package: "",
    version: "",
    estimatedDate: null,
    deliveryDate: null,
    isCustom: true,
};

export const KiiperBanking = (props) => {
    // Properties
    const { setTexto, setalertType, setShow, companyID, setBackDrop, data, reloadProjects, serviceID } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [banking, setBanking] = useState(defaultState);
    const [isError, setIsError] = useState(false);

    /** Component functions */
    useEffect(() => {
        console.log(data);
        setBanking({
            hours: data.consulting_hours ?? 1,
            ogFee: data.original_fee ?? data.fee,
            fee: (data.original_fee ?? data.fee) * (data.consulting_hours ?? 1),
            discount: data.discount ?? 0,
            totalFee: (data.original_fee ?? data.fee) * (data.consulting_hours ?? 1) * (1 - (data.discount ?? 0) / 100),
            package: data.package ?? "",
            version: data.version ?? "",
            estimatedDate: data.client_commitment_date
                ? moment(data.client_commitment_date).utc()
                : null,
            deliveryDate: data.client_delivery_date ? moment(data.client_delivery_date).utc() : null,
            isCustom: data.package !== 4,
        });
    }, [data]);

    const handleChange = (event) => {
        setBanking({
            ...banking,
            [event.target.name]: event.target.value,
            fee: event.target.value * banking.ogFee,
            totalFee: event.target.value * banking.ogFee * (1 - banking.discount / 100),
        });
    };

    const handleChangeDiscount = (event) => {
        let discountAux = event.target.value >= 0 && event.target.value <= 100 ? event.target.value : banking.discount;
        setBanking({
            ...banking,
            discount: discountAux,
            totalFee: banking.fee * (1 - discountAux / 100),
        });
    };

    const updateProject = async () => {
        setBackDrop(true);

        if (banking.version && banking.package && banking.estimatedDate) {
            let params = {
                company_id: companyID,
                service_id: serviceID,
                project_id: data.project_id,
                project_company_id: data.project_company_id,
                client_delivery_date: banking.deliveryDate
                    ? moment(banking.deliveryDate).startOf("day").format()
                    : null,
                client_commitment_date: moment(banking.estimatedDate).startOf("day").format(),
                discount: banking.discount ? parseFloat(banking.discount) : 0,
                fee: banking.totalFee,
                package: banking.package,
                version: banking.version,
                consulting_hours: parseFloat(banking.hours),
            };

            await fetch(`/addServicesAndProjects`, {
                method: "POST",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
                body: JSON.stringify(params),
            })
                .then((response) => {
                    if (response.ok) {
                        setTexto(t("miscellaneous.successfulUpdate"));
                        setalertType("success");
                        setShow(true);
                        reloadProjects(true);
                    } else {
                        setTexto(t("miscellaneous.updatingError"));
                        setalertType("error");
                        setShow(true);
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            setTexto(t("miscellaneous.requiredData"));
            setalertType("warning");
            setShow(true);
            setIsError(true);
        }

        setBackDrop(false);
    };

    const handleChangeEstimatedDate = (date) => {
        if (!date || !banking.deliveryDate) {
            setBanking({
                ...banking,
                estimatedDate: date,
            });
        } else {
            if (date.isAfter(banking.deliveryDate.format("YYYY-MM-DD"))) {
                setBanking({
                    ...banking,
                    estimatedDate: null,
                });

                setTexto(t("reports.meetingDateMess"));
                setalertType("warning");
                setShow(true);
            } else {
                setBanking({
                    ...banking,
                    estimatedDate: date,
                });
            }
        }
    };

    const handleChangeDeliveryDate = (date) => {
        if (!date || !banking.estimatedDate) {
            setBanking({
                ...banking,
                deliveryDate: date,
            });
        } else {
            if (date.isBefore(banking.estimatedDate.format("YYYY-MM-DD"))) {
                setBanking({
                    ...banking,
                    deliveryDate: null,
                });

                setTexto(t("reports.meetingDateMess"));
                setalertType("warning");
                setShow(true);
            } else {
                setBanking({
                    ...banking,
                    deliveryDate: date,
                });
            }
        }
    };

    const handleChangePackage = (event) => {
        let hoursNew = 1;

        if (event.target.value === 1) {
            hoursNew = 4;
        } else if (event.target.value === 2) {
            hoursNew = 8;
        } else if (event.target.value === 3) {
            hoursNew = 16;
        }

        setBanking({
            ...banking,
            package: event.target.value,
            hours: hoursNew,
            fee: hoursNew * banking.ogFee,
            totalFee: hoursNew * banking.ogFee * (1 - banking.discount / 100),
            isCustom: event.target.value !== 4,
        });
    };

    const handleChangeVersion = (event) => {
        setBanking({
            ...banking,
            version: event.target.value,
        });
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0}>
                    <CardHeader title="Kiiper Banking setup" />
                    <Divider />
                    <CardContent>
                        <Grid container alignItems="flex-start" spacing={2}>
                            <Grid item xs={12} sm={6} lg={2}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-ogFee">
                                        {t("services.hourlyRate")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-ogFee"
                                        value={banking.ogFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRate")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={1}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.hours")}
                                    name="hours"
                                    onChange={handleChange}
                                    value={banking.hours}
                                    variant="outlined"
                                    type="number"
                                    disabled={banking.isCustom}
                                    inputProps={{
                                        min: 1,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-fee">{t("services.projectFee")}</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-fee"
                                        value={banking.fee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.projectFee")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.discount")}
                                    value={banking.discount}
                                    name="discount"
                                    onChange={handleChangeDiscount}
                                    onBlur={() =>
                                        setBanking({
                                            ...banking,
                                            discount: parseFloat(
                                                banking.discount === "" ? 0 : banking.discount
                                            ).toFixed(2),
                                        })
                                    }
                                    variant="outlined"
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 100,
                                        },
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    sx={{
                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                            display: "none",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-totalFee">
                                        {t("proposals.feeTotal")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-totalFee"
                                        value={banking.totalFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("proposals.feeTotal")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <Card elevation={0}>
                    <CardHeader title={t("services.serviceResponsibility")} sx={{ color: "#ff5968" }} />
                    <Divider />
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth required error={isError && banking.package === ""}>
                                    <InputLabel id="ver-simple-select-label">{t("services.package")}</InputLabel>
                                    <Select
                                        name="package"
                                        labelId="ver-simple-select-label"
                                        IconComponent={KeyboardArrowDownIcon}
                                        value={banking.package}
                                        label={t("services.package")}
                                        onChange={handleChangePackage}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    mt: 0.7,
                                                    borderRadius: 2,
                                                    boxShadow: "10px 10px 15px 0px rgba(3,24,81,0.15)",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value={1}>Basic (4 hrs)</MenuItem>
                                        <MenuItem value={2}>Professional (8 hrs)</MenuItem>
                                        <MenuItem value={3}>Advanced (16 hrs)</MenuItem>
                                        <MenuItem value={4}>{t("currentView.personal")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth required error={isError && banking.version === ""}>
                                    <InputLabel id="ver-simple-select-label">{t("services.version")}</InputLabel>
                                    <Select
                                        name="version"
                                        labelId="ver-simple-select-label"
                                        IconComponent={KeyboardArrowDownIcon}
                                        value={banking.version}
                                        label={t("services.version")}
                                        onChange={handleChangeVersion}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    mt: 0.7,
                                                    borderRadius: 2,
                                                    boxShadow: "10px 10px 15px 0px rgba(3,24,81,0.15)",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value={"Plan 5"}>Plan 5</MenuItem>
                                        <MenuItem value={"Plan 15"}>Plan 15</MenuItem>
                                        <MenuItem value={"Plan 30"}>Plan 30</MenuItem>
                                        <MenuItem value={"Plan 50"}>Plan 50</MenuItem>
                                        <MenuItem value={"Plan 200"}>Plan 200</MenuItem>
                                        <MenuItem value={"Plan 500"}>Plan 500</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    value={banking.estimatedDate}
                                    onChange={(newValue) => handleChangeEstimatedDate(newValue)}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            required
                                            fullWidth
                                            {...params}
                                            error={isError && !banking.estimatedDate}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("services.estimDeliveryDate")}
                                    views={["year", "month", "day"]}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    value={banking.deliveryDate}
                                    onChange={(newValue) => handleChangeDeliveryDate(newValue)}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth {...params} />}
                                    inputProps={{ readOnly: true }}
                                    label={t("services.deliveryDate")}
                                    views={["year", "month", "day"]}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                    <Divider />
                    <Box className={classes.buttonContainer}>
                        <Button
                            color="secondary"
                            variant="contained"
                            disableElevation
                            classes={{ root: classes.btnRoot }}
                            onClick={updateProject}
                        >
                            {t("miscellaneous.save")}
                        </Button>
                    </Box>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
